import {
  sourcesToHighlightsAndCuepoints,
  extractSectionsFromHighlightAsset,
  transformKeysToCamelCase,
  type Section,
} from './index';
import type { StoryboardHighlights } from './types';

export interface ExperienceAssets<H = unknown, C = unknown> {
  /** Chapters for the video timeline. */
  sections: Section[];
  /** Cuepoints joining video time → highlight (`highlightId` → `id`). */
  cuepoints: C[];
  /** PDF highlights to render + scroll to during playback. */
  highlights: H[];
}

const toCamelArray = <T>(data: unknown): T[] =>
  data == null
    ? []
    : (transformKeysToCamelCase(data as Record<string, unknown>) as T[]);

/**
 * Pure, SSR-safe parse of a publication's already-fetched highlight asset
 * (the `section_highlights` model) into the three arrays both panels of
 * the experience page need: `sections` (video chapters), `cuepoints` (the
 * video↔document join) and `highlights` (PDF annotations).
 *
 * Single source of truth for the asset shape so the video player and the
 * PDF viewer can't drift. Callers fetch the JSON their own way (server-side
 * in sales, client-side in admin) and pass it here; an absent/empty asset
 * yields empty arrays (the video still plays, just without chapters/sync).
 *
 * Generic over the highlight/cuepoint result types so the consuming app
 * can label them (e.g. `buildExperienceAssets<IHighlight, Cuepoint>(…)`)
 * without this framework-agnostic lib depending on those types.
 */
export function buildExperienceAssets<H = unknown, C = unknown>(
  highlightAsset: StoryboardHighlights | null | undefined,
): ExperienceAssets<H, C> {
  if (!highlightAsset?.section_highlights) {
    return { sections: [], cuepoints: [], highlights: [] };
  }

  const { highlights, cuepoints } = sourcesToHighlightsAndCuepoints(
    highlightAsset.section_highlights,
    highlightAsset.document_id,
  );

  return {
    sections: extractSectionsFromHighlightAsset(
      highlightAsset.section_map,
      highlightAsset.section_highlights,
      highlightAsset.section_timestamps,
    ),
    highlights: toCamelArray<H>(highlights.data),
    cuepoints: toCamelArray<C>(cuepoints.data),
  };
}
